import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Calendar, MapPin, Trash2, Printer, CalendarDown } from "lucide-react";
import DayTimeline from "@/components/trip/DayTimeline";
import FlightsLog from "@/components/trip/FlightsLog";
import HotelsLog from "@/components/trip/HotelsLog";
import BudgetTracker from "@/components/trip/BudgetTracker";
import PackingList from "@/components/trip/PackingList";
import MapView from "@/components/trip/MapView";
import ShareTab from "@/components/trip/ShareTab";
import DocumentsVault from "@/components/trip/DocumentsVault";
import CoverPickerDialog from "@/components/trip/CoverPickerDialog";
import WeatherStrip from "@/components/trip/WeatherStrip";
import TripChat from "@/components/trip/TripChat";
import SouvenirsBoard from "@/components/trip/SouvenirsBoard";

export default function TripDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const { data } = await api.get(`/trips/${id}`); setTrip(data); }
    catch { toast.error("Trip not found"); navigate("/dashboard"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const del = async () => {
    if (!window.confirm("Delete this trip? This cannot be undone.")) return;
    try { await api.delete(`/trips/${id}`); toast.success("Trip deleted"); navigate("/dashboard"); }
    catch { toast.error("Failed"); }
  };

  if (loading || !trip) return (
    <div className="min-h-screen bg-[#FAF7F2]"><Navbar/><div className="max-w-7xl mx-auto p-8 text-stone-500 font-mono-code">Loading trip…</div></div>
  );

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <Navbar/>
      {/* Hero */}
      <div className="relative">
        <div className="h-72 sm:h-80 overflow-hidden bg-stone-900">
          <img src={trip.cover_image || "https://images.unsplash.com/photo-1534445867742-43195f401b6c?crop=entropy&cs=srgb&fm=jpg&q=85"} className="w-full h-full object-cover opacity-70"/>
          <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 to-transparent"/>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative -mt-40 pb-6 text-white">
          <button onClick={()=>navigate("/dashboard")} className="inline-flex items-center gap-1 text-sm text-white/80 hover:text-white mb-4" data-testid="back-to-dashboard">
            <ArrowLeft size={14}/> Dashboard
          </button>
          <div className="font-mono-code text-xs uppercase tracking-[0.3em] text-amber-300 mb-2">Trip journal · {trip.destination}</div>
          <div className="flex items-start justify-between gap-4">
            <h1 className="font-serif-display text-4xl sm:text-6xl font-bold max-w-3xl leading-[1.05]" data-testid="trip-title">{trip.title}</h1>
            <div className="flex gap-2 flex-wrap justify-end">
              <CoverPickerDialog trip={trip} onUpdate={setTrip}/>
              <Button
                variant="outline" size="sm"
                onClick={() => {
                  const token = localStorage.getItem("wl_token");
                  window.open(`${process.env.REACT_APP_BACKEND_URL}/api/trips/${trip.trip_id}/calendar.ics?auth=${encodeURIComponent(token || "")}`, "_blank");
                }}
                className="text-white border-white/40 bg-white/10 hover:bg-white/20 hover:text-white"
                data-testid="calendar-ics-button"
              >
                <Calendar size={14} className="mr-1"/> Add to Calendar
              </Button>
              <Button variant="outline" size="sm" onClick={()=>window.open(`/trips/${trip.trip_id}/print`, "_blank")} className="text-white border-white/40 bg-white/10 hover:bg-white/20 hover:text-white" data-testid="print-trip-button">
                <Printer size={14} className="mr-1"/> Export PDF
              </Button>
              {trip._role !== "collaborator" && (
                <Button variant="outline" size="sm" onClick={del} className="text-white border-white/40 bg-white/10 hover:bg-white/20 hover:text-white" data-testid="delete-trip-button">
                  <Trash2 size={14} className="mr-1"/> Delete
                </Button>
              )}
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-white/90">
            <span className="flex items-center gap-1.5"><MapPin size={14}/> {trip.destination}</span>
            <span className="flex items-center gap-1.5"><Calendar size={14}/> {trip.days.length} days</span>
            {trip.start_date && <span className="font-mono-code text-xs">{trip.start_date} → {trip.end_date}</span>}
            {trip._role === "collaborator" && <span className="font-mono-code text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/15 border border-white/25">Shared · {trip._permission}</span>}
          </div>
          {trip.description && <p className="mt-4 text-white/80 max-w-2xl">{trip.description}</p>}
          <div className="mt-6">
            <WeatherStrip destination={trip.destination} start={trip.start_date}/>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <Tabs defaultValue="itinerary">
          <TabsList className="bg-stone-100 flex flex-wrap h-auto">
            <TabsTrigger value="itinerary" data-testid="trip-tab-itinerary">Itinerary</TabsTrigger>
            <TabsTrigger value="flights" data-testid="trip-tab-flights">Flights</TabsTrigger>
            <TabsTrigger value="hotels" data-testid="trip-tab-hotels">Stays</TabsTrigger>
            <TabsTrigger value="budget" data-testid="trip-tab-budget">Budget</TabsTrigger>
            <TabsTrigger value="packing" data-testid="trip-tab-packing">Packing</TabsTrigger>
            <TabsTrigger value="documents" data-testid="trip-tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="map" data-testid="trip-tab-map">Map</TabsTrigger>
            <TabsTrigger value="souvenirs" data-testid="trip-tab-souvenirs">Souvenirs</TabsTrigger>
            <TabsTrigger value="share" data-testid="trip-tab-share">Share</TabsTrigger>
          </TabsList>
          <TabsContent value="itinerary" className="mt-6"><DayTimeline trip={trip} onUpdate={setTrip}/></TabsContent>
          <TabsContent value="flights" className="mt-6"><FlightsLog trip={trip} onUpdate={setTrip}/></TabsContent>
          <TabsContent value="hotels" className="mt-6"><HotelsLog trip={trip} onUpdate={setTrip}/></TabsContent>
          <TabsContent value="budget" className="mt-6"><BudgetTracker trip={trip} onUpdate={setTrip}/></TabsContent>
          <TabsContent value="packing" className="mt-6"><PackingList trip={trip} onUpdate={setTrip}/></TabsContent>
          <TabsContent value="documents" className="mt-6"><DocumentsVault trip={trip} onUpdate={setTrip}/></TabsContent>
          <TabsContent value="map" className="mt-6"><MapView trip={trip}/></TabsContent>
          <TabsContent value="souvenirs" className="mt-6"><SouvenirsBoard trip={trip} onUpdate={setTrip}/></TabsContent>
          <TabsContent value="share" className="mt-6"><ShareTab trip={trip} onUpdate={setTrip}/></TabsContent>
        </Tabs>
      </main>
      <TripChat trip={trip}/>
    </div>
  );
}
